import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle, XCircle, Eye, EyeOff, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ActivateSalesRepPage = () => {
    const [searchParams] = useSearchParams();
    const { orgCode } = useParams();
    const navigate = useNavigate();
    
    const token = searchParams.get('token');
    
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [inviteData, setInviteData] = useState(null);
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('Kein Aktivierungstoken gefunden');
            setLoading(false);
            return;
        }
        
        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            const response = await axios.get(`${API}/admin/sales-invites/validate/${token}`);
            setInviteData(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Ungültiger oder abgelaufener Aktivierungslink');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 6) {
            toast.error('Passwort muss mindestens 6 Zeichen haben');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        
        setSubmitting(true);
        
        try {
            await axios.post(`${API}/admin/sales-invites/activate`, {
                token,
                password
            });
            
            setSuccess(true);
            toast.success('Ihr Konto wurde erfolgreich aktiviert!');
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate(`/o/${orgCode}/login`);
            }, 3000);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Aktivierung fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    const primaryColor = inviteData?.organization?.primary_color || '#3b82f6';

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-slate-400" />
                    <p className="text-slate-500">Link wird überprüft...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 text-center">
                        <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">
                            Aktivierung nicht möglich
                        </h2>
                        <p className="text-slate-600 mb-6">{error}</p>
                        <Button 
                            variant="outline" 
                            onClick={() => navigate(`/o/${orgCode}/login`)}
                        >
                            Zur Anmeldung
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 text-center">
                        <CheckCircle 
                            className="h-16 w-16 mx-auto mb-4" 
                            style={{ color: primaryColor }} 
                        />
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">
                            Konto aktiviert!
                        </h2>
                        <p className="text-slate-600 mb-6">
                            Ihr Konto wurde erfolgreich aktiviert. Sie werden zur Anmeldung weitergeleitet...
                        </p>
                        <Button 
                            style={{ backgroundColor: primaryColor }}
                            onClick={() => navigate(`/o/${orgCode}/login`)}
                        >
                            Jetzt anmelden
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Activation form
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    {inviteData?.organization?.logo && (
                        <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${inviteData.organization.logo}`}
                            alt={inviteData.organization.name}
                            className="h-12 object-contain mx-auto mb-4"
                        />
                    )}
                    <div 
                        className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                        style={{ backgroundColor: `${primaryColor}15` }}
                    >
                        <UserCheck size={28} style={{ color: primaryColor }} />
                    </div>
                    <CardTitle className="text-2xl">Konto aktivieren</CardTitle>
                    <CardDescription>
                        Willkommen bei {inviteData?.organization?.name}! Legen Sie Ihr Passwort fest, um Ihr Vertriebskonto zu aktivieren.
                    </CardDescription>
                </CardHeader>
                
                <CardContent>
                    <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                        <div className="text-sm text-slate-500 mb-1">Name</div>
                        <div className="font-medium text-slate-900">{inviteData?.name}</div>
                        <div className="text-sm text-slate-500 mt-3 mb-1">E-Mail</div>
                        <div className="font-medium text-slate-900">{inviteData?.email}</div>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">Neues Passwort</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Mindestens 6 Zeichen"
                                    required
                                    data-testid="password-input"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                            <Input
                                id="confirmPassword"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Passwort erneut eingeben"
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>
                        
                        <Button
                            type="submit"
                            className="w-full"
                            style={{ backgroundColor: primaryColor }}
                            disabled={submitting}
                            data-testid="activate-btn"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Aktiviere...
                                </>
                            ) : (
                                'Konto aktivieren'
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};
